import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { DocManiaLogo, DocManiaLogoImage } from '../components/DocManiaLogo';
import { Lock, Loader2, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(token ? 'form' : 'missing-token');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (status !== 'success') return undefined;
    const timer = setTimeout(() => navigate('/login'), 2500);
    return () => clearTimeout(timer);
  }, [status, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setErrorMessage('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }

    setLoading(true);
    setErrorMessage('');
    try {
      const response = await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: password,
      });
      if (response.data.success) {
        setStatus('success');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage(
        error.response?.data?.detail ||
          'Invalid or expired reset link. Please request a new one.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (status === 'missing-token') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-brand-hero">
        <Card className="w-full max-w-md card-elevated animate-fade-in">
          <CardContent className="pt-10 pb-10 text-center space-y-6">
            <DocManiaLogoImage size={48} className="mx-auto shadow-md" />
            <div className="h-20 w-20 mx-auto rounded-full bg-destructive/10 flex items-center justify-center">
              <XCircle className="h-10 w-10 text-destructive" />
            </div>
            <div className="space-y-2">
              <h1 className="text-2xl font-bold text-foreground">Invalid Reset Link</h1>
              <p className="text-muted-foreground">
                Invalid or missing reset link. Please request a new password reset.
              </p>
            </div>
            <Link to="/forgot-password">
              <Button className="w-full glow-button" data-testid="request-new-reset-btn">
                Request New Reset Link
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="ghost" className="w-full" data-testid="back-to-login-btn">
                Back to Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-brand-hero">
        <Card className="w-full max-w-md card-elevated animate-fade-in">
          <CardContent className="pt-10 pb-10 text-center space-y-6">
            <DocManiaLogoImage size={48} className="mx-auto shadow-md" />
            <div className="h-20 w-20 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
              <CheckCircle className="h-10 w-10 text-primary" />
            </div>
            <div className="space-y-2">
              <h1 className="text-2xl font-bold text-foreground">Password Reset</h1>
              <p className="text-muted-foreground">
                Your password has been reset successfully. Redirecting to login…
              </p>
            </div>
            <Button
              className="w-full glow-button"
              onClick={() => navigate('/login')}
              data-testid="go-to-login-btn"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-brand-hero">
        <Card className="w-full max-w-md card-elevated animate-fade-in">
          <CardContent className="pt-10 pb-10 text-center space-y-6">
            <DocManiaLogoImage size={48} className="mx-auto shadow-md" />
            <div className="h-20 w-20 mx-auto rounded-full bg-destructive/10 flex items-center justify-center">
              <XCircle className="h-10 w-10 text-destructive" />
            </div>
            <div className="space-y-2">
              <h1 className="text-2xl font-bold text-foreground">Reset Failed</h1>
              <p className="text-muted-foreground">{errorMessage}</p>
            </div>
            <Link to="/forgot-password">
              <Button className="w-full glow-button" data-testid="request-new-reset-btn">
                Request New Reset Link
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="ghost" className="w-full" data-testid="back-to-login-btn">
                Back to Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-brand-hero">
      <Card className="w-full max-w-md card-elevated animate-fade-in">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <DocManiaLogoImage size={56} className="shadow-md" />
          </div>
          <div className="space-y-2">
            <CardTitle className="text-2xl font-bold">Reset Password</CardTitle>
            <CardDescription className="text-muted-foreground">
              Choose a new password for your <DocManiaLogo size="small" /> account
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  data-testid="reset-password-input"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  minLength={6}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  data-testid="reset-password-confirm-input"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10"
                  minLength={6}
                  required
                />
              </div>
            </div>

            {errorMessage && status === 'form' && (
              <p className="text-sm text-destructive">{errorMessage}</p>
            )}

            <Button
              type="submit"
              data-testid="reset-password-submit-btn"
              className="w-full glow-button"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Resetting...
                </>
              ) : (
                'Reset Password'
              )}
            </Button>

            <div className="text-center text-sm text-muted-foreground">
              <Link
                to="/login"
                className="text-primary hover:underline font-medium"
                data-testid="back-to-login-link"
              >
                Back to login
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
